import { useEffect, useRef } from 'react';
import { driverSupabase as supabase } from '@/integrations/supabase/driver-client';

/**
 * Centralized Realtime connection health monitor.
 *
 * Mounted once at the auth-provider level. When the device returns from
 * background or the network reconnects, we force the Realtime socket to
 * reconnect so all subscribed channels resume delivering events.
 *
 * Without this, on flaky Indian mobile networks (Jio etc.) Realtime can stay
 * silently CLOSED for minutes — drivers miss fare updates, cancellations,
 * and trip offers until a manual refresh.
 */
export function useRealtimeHealth() {
  const lastReconnectRef = useRef<number>(0);

  useEffect(() => {
    const reconnect = (reason: string) => {
      // Throttle: at most once every 3 seconds
      const now = Date.now();
      if (now - lastReconnectRef.current < 3000) return;
      lastReconnectRef.current = now;

      try {
        const rt: any = (supabase as any).realtime;
        const isOpen = rt?.isConnected?.();
        if (!isOpen) {
          console.log(`[Realtime] Reconnecting (${reason})…`);
          rt?.disconnect?.();
          rt?.connect?.();
        }
      } catch (err) {
        console.warn('[Realtime] Reconnect attempt failed:', err);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') reconnect('visibilitychange');
    };
    const onOnline = () => reconnect('online');

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    window.addEventListener('focus', () => reconnect('focus'));

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
    };
  }, []);
}
